/**
 * Getting-started checklist — Zod schemas.
 *
 * The server computes which steps a tenant has completed based on
 * actual state (is there ≥1 admin? ≥1 application? ≥1 passkey?
 * directory connected? webhook configured?). The UI doesn't
 * guess — it renders whatever the server says.
 */

import { z } from "zod";

export const setupStepIdSchema = z.enum([
  "invite_admins",
  "register_application",
  "configure_branding",
  "enroll_users",
  "connect_directory",
  "configure_policies",
  "wire_webhook",
]);
export type SetupStepId = z.infer<typeof setupStepIdSchema>;

export const setupStepSchema = z.object({
  id: setupStepIdSchema,
  status: z.enum(["pending", "in_progress", "complete", "skipped"]),
  /** Optional metrics the server computes for display. */
  metrics: z
    .object({
      current: z.number().int().nonnegative(),
      target: z.number().int().nonnegative(),
    })
    .nullable(),
  /**
   * connect_directory on enterprise_fed tenants only: per-org
   * sign-in-federation coverage, so the step can say WHICH active
   * organizations still lack an enabled provider. The name list is
   * server-capped; the counts stay exact when it is truncated.
   */
  idp_orgs: z
    .object({
      active_orgs: z.number().int().nonnegative(),
      orgs_with_provider: z.number().int().nonnegative(),
      unconfigured_orgs: z.array(z.string()),
    })
    .optional(),
});
export type SetupStep = z.infer<typeof setupStepSchema>;

export const setupStatusSchema = z.object({
  steps: z.array(setupStepSchema),
  completion_percent: z.number().int().min(0).max(100),
});
export type SetupStatus = z.infer<typeof setupStatusSchema>;
